<template>
  <div class="h-full min-h-screen flex flex-col bg-slate-50 overflow-hidden">
    <SplashScreen v-if="!isReady" :message="splashMessage" :error="bootError" @retry="bootstrap" />

    <template v-else>
      <header class="shrink-0 h-14 flex items-center justify-between px-4 bg-[#0d4169] text-white shadow-md">
        <div class="flex items-center gap-2 min-w-0">
          <button
            v-if="showBack"
            type="button"
            class="p-1.5 rounded-lg hover:bg-white/10 transition"
            aria-label="Back"
            @click="goBack"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <!-- <img src="./assets/logo.png" alt="Ahun" class="h-8 w-auto object-contain brightness-0 invert" /> -->
          <div class="min-w-0">
            <p class="text-sm font-black truncate leading-tight">Ahun Jobs</p>
            <p v-if="displayName" class="text-[10px] text-white/70 truncate">{{ displayName }}</p>
          </div>
        </div>
        <!-- Employer / general: Home. Employee: My Applications, or Back when already there -->
        <button
          v-if="!isEmployee"
          type="button"
          class="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition"
          @click="$router.push({ name: 'welcome' })"
        >
          Home
        </button>
        <button
          v-else-if="!isOnMyApplications"
          type="button"
          class="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition"
          @click="goMyApplications"
        >
          My Applications
        </button>
        <button
          v-else
          type="button"
          class="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition"
          @click="goBack"
        >
          Back
        </button>
      </header>

      <main class="flex-1 overflow-y-auto">
        <router-view />
      </main>

      <nav
        v-if="!isEmployee"
        class="shrink-0 grid gap-1 border-t border-slate-200 bg-white px-2 py-2 safe-bottom"
        :class="isEmployer ? 'grid-cols-2' : 'grid-cols-3'"
      >
        <button
          type="button"
          class="flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] font-bold transition"
          :class="isActive('welcome') ? 'text-[#0d4169] bg-[#e9f2f8]' : 'text-slate-500'"
          @click="$router.push({ name: 'welcome' })"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          Home
        </button>
        <button
          type="button"
          class="flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] font-bold transition"
          :class="isMyJobsNavActive ? 'text-[#0d4169] bg-[#e9f2f8]' : 'text-slate-500'"
          @click="$router.push({ name: 'my_jobs' })"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
          My Company Jobs
        </button>
        <button
          v-if="!isEmployer"
          type="button"
          class="flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] font-bold transition"
          :class="isActive('my_applications') ? 'text-[#0d4169] bg-[#e9f2f8]' : 'text-slate-500'"
          @click="$router.push({ name: 'my_applications' })"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          Applications
        </button>
      </nav>
    </template>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SplashScreen from './components/SplashScreen.vue'
import { applyStartParamRoute, getTelegramUser, initTelegramUi, parseStartParam, waitForStartParam } from './utils/telegram'

export default {
  name: 'App',
  components: { SplashScreen },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const splashMessage = ref('Preparing your workspace…')
    const bootError = ref('')

    const isReady = computed(() => store.getters.isReady)
    const displayName = computed(() => {
      const u = store.getters.currentUser
      if (!u) return ''
      return u.full_name || (u.username ? `@${u.username}` : `TG ${u.telegram_id}`)
    })
    // Employee: Telegram Apply / My Applications entry, or employee-only screens
    const isEmployee = computed(() => {
      const parsed = parseStartParam(store.getters.startParam)
      if (parsed.type === 'job' || parsed.type === 'my_applications') return true
      return ['apply_job', 'my_applications', 'application_detail'].includes(route.name)
    })
    // Employer: Post Job / My Jobs / View Applicants entry, or employer-only screens
    const isEmployer = computed(() => {
      if (isEmployee.value) return false
      const parsed = parseStartParam(store.getters.startParam)
      if (parsed.type === 'post_job' || parsed.type === 'my_jobs' || parsed.type === 'applicants') return true
      return ['post_job', 'my_jobs', 'applicants', 'job_detail'].includes(route.name)
    })
    const isOnMyApplications = computed(() => route.name === 'my_applications')
    const isMyJobsNavActive = computed(() =>
      ['my_jobs', 'post_job', 'applicants', 'job_detail'].includes(route.name)
    )
    const showBack = computed(() => route.name !== 'welcome')

    function isActive(name) {
      return route.name === name
    }

    function goBack() {
      if (window.history.length > 1) router.back()
      else router.push({ name: 'welcome' })
    }

    function goMyApplications() {
      router.push({ name: 'my_applications' })
    }

    async function bootstrap() {
      initTelegramUi()
      const startParam = await waitForStartParam()
      store.commit('setStartParam', startParam)
      splashMessage.value = 'Welcome to Ahun Jobs'

      try {
        const tgUser = getTelegramUser()
        if (!tgUser?.telegram_id) {
          throw new Error('Open this app from Telegram to continue.')
        }

        splashMessage.value = 'loading...'
        await store.dispatch('loginWithTelegram', {
          telegram_id: tgUser.telegram_id,
          username: tgUser.username,
          full_name: tgUser.full_name,
          first_name: tgUser.first_name,
          last_name: tgUser.last_name,
        })
      } catch (err) {
        console.error(err)
        if (!store.getters.isAuthenticated) {
          bootError.value = err.message || 'Could not connect. Please try again.'
          return
        }
      }

      localStorage.setItem('Ahun_last_activity', String(Date.now()))
      applyStartParamRoute(router, startParam)
      store.commit('setReady', true)
    }

    onMounted(bootstrap)

    return {
      isReady,
      splashMessage,
      bootError,
      displayName,
      isEmployee,
      isEmployer,
      isOnMyApplications,
      isMyJobsNavActive,
      showBack,
      isActive,
      goBack,
      goMyApplications,
      bootstrap,
    }
  },
}
</script>

<style>
.safe-bottom {
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
}
</style>
